import {Divider, Stack, Typography} from '@mui/material';
import {ErrorState, SentinelLoading} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {GuideEmptyState} from 'presentation/modules/guide/shared/ui/components/GuideEmptyState';
import {translations} from 'presentation/translations-new';
import React from 'react';
import {HealthGuideCard} from 'presentation/modules/guide/health/ui/pages/HealthGuide/components/HealthGuideCard';
import {HealthGuideDetails} from 'presentation/modules/guide/health/ui/pages/HealthGuide/components/HealthGuideDetails';
import {NetworkAlert} from 'presentation/modules/guide/health/ui/pages/HealthGuide/components/NetworkAlert';
import {TelehealthCard} from 'presentation/modules/guide/health/ui/pages/HealthGuide/components/TelehealthCard';
import {NetworkTypes} from 'presentation/modules/guide/health/ui/pages/HealthGuide/types';
import {useHealthProviderResults} from './hooks/useHealthProviderResults/useHealthProviderResults';
import {Container, ContentCards} from './styles';
import {useHostName} from 'presentation/hooks';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {
	failToLoadTitle,
	failToLoadDescription,
	loading,
	telemedicine: telemedicineLabel,
} = translations['pt-br'].commons;

const {titleOwnNetwork, titleAccreditedNetwork, titleNotreDameHapvidaNetwork} =
	translations['pt-br'].privateFlow.guideFlow;

export function ProvidersResultsV3() {
	const {
		isLoading,
		isError,
		refetch,
		isEmpty,
		showTelehealth,
		shouldReverseNetworkOrder,
		redeCredenciada,
		redePropria,
		showTradeNameFlag,
		loadMore,
		isFetchingNextPage,
		openDetails,
		setOpenDetails,
		provider,
		setProvider,
		showSelfNetwork,
		showAccreditedNetwork,
		showDivider,
		showNetworkAlert,
	} = useHealthProviderResults();

	const {isNotredameSaude} = useHostName();

	const {congenereEligibility} = useCongenereEligibilityStore();

	if (isLoading) {
		return <Loading text={loading} minHeight="45%" />;
	}

	if (isError) {
		return (
			<ErrorState
				title={failToLoadTitle}
				description={failToLoadDescription}
				handleRefresh={refetch}
			/>
		);
	}

	if (isEmpty) {
		return <GuideEmptyState />;
	}

	return (
		<Container>
			{showTelehealth && !congenereEligibility ? (
				<Stack mb={2} gap={1}>
					<Typography variant="subtitle1" color="text.secondary" fontWeight={600}>
						{telemedicineLabel}
					</Typography>
					<TelehealthCard />
				</Stack>
			) : null}

			<Stack flexDirection={shouldReverseNetworkOrder ? 'column-reverse' : 'column'}>
				{showSelfNetwork ? (
					<Stack gap={0.5}>
						<Typography
							variant="subtitle1"
							fontWeight={600}
							color="text.secondary"
							mb={showNetworkAlert ? 0 : 1.5}>
							{isNotredameSaude ? titleNotreDameHapvidaNetwork : titleOwnNetwork}
						</Typography>
						{showNetworkAlert ? (
							<NetworkAlert
								type={
									isNotredameSaude
										? NetworkTypes.NOTRE_DAME_HAPVIDA
										: NetworkTypes.SELF_NETWORK
								}
							/>
						) : null}

						<ContentCards>
							{redePropria.map((provider, index) => {
								return (
									<HealthGuideCard
										key={`${provider.nmPrestador}-${index}`}
										provider={provider}
										showTradeName={showTradeNameFlag}
										handleOpenDetails={() => {
											setProvider(provider);
											setOpenDetails(true);
										}}
									/>
								);
							})}
						</ContentCards>
					</Stack>
				) : null}

				{showDivider ? <Divider sx={{mt: 3, mb: 2}} /> : null}

				{showAccreditedNetwork ? (
					<Stack gap={0.5}>
						{!isNotredameSaude && (
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="text.secondary"
								mb={showNetworkAlert ? 0 : 1.5}>
								{titleAccreditedNetwork}
							</Typography>
						)}
						{showNetworkAlert ? (
							<NetworkAlert
								type={
									isNotredameSaude
										? NetworkTypes.ACCREDITED_NOTRE_DAME
										: NetworkTypes.ACCREDITED_NETWORK
								}
							/>
						) : null}

						<ContentCards>
							{redeCredenciada.map((provider, index) => {
								return (
									<HealthGuideCard
										key={`${provider.nmPrestador}-${index}`}
										provider={provider}
										showTradeName={showTradeNameFlag}
										handleOpenDetails={() => {
											setProvider(provider);
											setOpenDetails(true);
										}}
									/>
								);
							})}
						</ContentCards>
					</Stack>
				) : null}
			</Stack>

			<HealthGuideDetails
				open={openDetails}
				handleClose={() => {
					setOpenDetails(false);
				}}
				provider={provider}
				showTradeName={showTradeNameFlag}
			/>

			<SentinelLoading
				minHeight="200px"
				showLoadingText={false}
				onVisible={loadMore}
				isLoading={isFetchingNextPage}
			/>
		</Container>
	);
}
